import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { useWaitlistCount } from '@/hooks/useWaitlistCount';

const MAX_FOUNDING = 10_000;

interface ReferralPanelProps {
  explorerNumber: number;
  referralCode: string;
}

const ReferralPanel = ({ explorerNumber, referralCode }: ReferralPanelProps) => {
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const referralLink = `dumbverse.app?ref=${referralCode}`;
  const { count } = useWaitlistCount();
  const remaining = Math.max(0, MAX_FOUNDING - count);

  useEffect(() => {
    const fetchCount = async () => {
      const { count } = await supabase
        .from('waitlist')
        .select('*', { count: 'exact', head: true })
        .eq('referred_by', referralCode);
      if (count !== null) setReferralCount(count);
    };
    fetchCount();
  }, [referralCode]);

  const copyLink = () => {
    navigator.clipboard.writeText(`https://${referralLink}`);
    setCopied(true);
    trackEvent('referral_link_copied');
    setTimeout(() => setCopied(false), 2000);
  };

  const tweetText = encodeURIComponent(
    `I just joined the Dumbverse as Explorer #${explorerNumber}.\n\nNine AI inhabitants respond to your dumbest ideas. No idea too dumb. No question too real.\n\nOnly ${remaining.toLocaleString()} founding spots left.\nJoin me → ${referralLink}\n\n@dumbverse`
  );
  const linkedInUrl = encodeURIComponent(`https://${referralLink}`);
  const whatsappText = encodeURIComponent(
    `I just joined the Dumbverse as Explorer #${explorerNumber}. Nine AI minds respond to every idea you post. Only ${remaining.toLocaleString()} founding spots left. Join me → https://${referralLink}`
  );

  return (
    <div className="mt-8 p-6 rounded-lg border border-border bg-card/50">
      <h3 className="font-bebas text-2xl text-foreground mb-2">Move up the list. Tell someone.</h3>

      <div className="flex items-center gap-2 mt-4 p-3 rounded bg-background/50 border border-border">
        <span className="flex-1 font-mono text-sm text-foreground truncate">{referralLink}</span>
        <button
          onClick={copyLink}
          className="px-4 py-1.5 rounded bg-primary text-primary-foreground font-mono text-xs hover:bg-primary/90 transition-colors shrink-0"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <p className="mt-3 text-sm font-mono text-muted-foreground">
        {referralCount} {referralCount === 1 ? 'person has' : 'people have'} joined through your link
      </p>

      <div className="flex flex-col sm:flex-row gap-3 mt-4">
        <a
          href={`https://twitter.com/intent/tweet?text=${tweetText}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('referral_shared_twitter')}
          className="flex-1 text-center py-2.5 rounded bg-card border border-border text-sm font-mono text-foreground hover:border-primary/40 transition-colors"
        >
          Share on X
        </a>
        <a
          href={`https://api.whatsapp.com/send?text=${whatsappText}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('referral_shared_whatsapp')}
          className="flex-1 text-center py-2.5 rounded bg-card border border-border text-sm font-mono text-foreground hover:border-primary/40 transition-colors"
        >
          Share on WhatsApp
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${linkedInUrl}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackEvent('referral_shared_linkedin')}
          className="flex-1 text-center py-2.5 rounded bg-card border border-border text-sm font-mono text-foreground hover:border-primary/40 transition-colors"
        >
          Share on LinkedIn
        </a>
        <button
          onClick={copyLink}
          className="flex-1 text-center py-2.5 rounded bg-card border border-border text-sm font-mono text-foreground hover:border-primary/40 transition-colors"
        >
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      <p className="mt-4 text-xs font-mono text-muted-foreground italic leading-relaxed">
        Every person who joins through your link moves you 5 spots up the founding list. Top 100 get lifetime Council access free.
      </p>
    </div>
  );
};

export default ReferralPanel;
